import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useLayoutEffect,
} from "react";
import { Row, Col, Button, Card, CardBody, Spinner } from "reactstrap";
import { useForm } from "react-hook-form";
import CommonForm, { FormFieldConfig } from "Components/Common/CommonForm";
import styles from "../../MainPrintListTabs/css/printlabel.module.scss";
import CommonTableIndexWise from "Components/Common/CommonTableIndexWise";
import tableStyles from "../../../../assets/scss/common/table.module.scss";
import sf from "../../../../assets/scss/common/searchFilter.module.scss";
import SimpleBar from "simplebar-react";
import { templateApiService } from "modules/Library/Template/api/template.api";
import DOMPurify from "dompurify";
import Barcode from "react-barcode";
import { createRoot } from "react-dom/client";
import { showToastMessage, formatDate } from "common/service/CommonService";
import { queryClient } from "api/queryClient";
import { useGlobalStudy } from "../../../../context/StudyContext";
import { dispensingColumns, dispensingFields } from "../helpers/dispensinglabel.constant";
import { useDispensingLabelService } from "../api/useDispensingLabelService";
import { DispensingLabelFormValues } from "../helpers/dispensinglabel.types";
import { dispensingService } from "modules/Pharmacy/Dispensing/api/dispensing.api";
import { useReturnsAccountability } from "modules/Pharmacy/ReturnsLog/api/useReturnsAccountability";
import { usePermission } from "context/PermissionContext";
import CommonButton from "Components/Common/CommonButton";
import { PERMISSION_ACTIONS, PERMISSION_MODULES } from "common/constants/PermissionConstants";
import GlobalStudyMismatchWarning from "../../../../Components/Common/GlobalStudyMismatchWarning";



const DispensingLabelList: React.FC = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedRows, setSelectedRows] = useState<any[]>([]);
  const [templateHtml, setTemplateHtml] = useState<string | null>(null);
  const [isTemplateLoading, setIsTemplateLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [searchFocused, setSearchFocused] = useState(false);
  const [individualCounts, setIndividualCounts] = useState<
    Record<number, number>
  >({});
  const previewRef = useRef<HTMLDivElement>(null);

  const {
    control,
    watch,
    setValue,
    formState: { errors },
  } = useForm<DispensingLabelFormValues>({
    defaultValues: {
      studyid: null,
      ipcategory: null,
      subjectno: null,
      ip: null,
      dosage_strength: null,
      batchno: null,
      expdate: null,
    },
  });

  const selectedStudyId = watch("studyid");
  const selectedIpCategory = watch("ipcategory");
  const selectedSubjectNo = watch("subjectno");

  const { selectedStudy } = useGlobalStudy();

  useEffect(() => {
    if (selectedStudy) {
      setValue("studyid", Number(selectedStudy.studyid));
    }
  }, [selectedStudy, setValue]);

  useEffect(() => {
    setValue("ipcategory", null);
    setValue("subjectno", null);
  }, [selectedStudyId, setValue]);

  const [inventoryList, setInventoryList] = useState<any[]>([]);
  const [isIpCategoryLoading, setIsIpCategoryLoading] = useState(false);

  useEffect(() => {
    if (!selectedStudyId) {
      setInventoryList([]);
      return;
    }

    setIsIpCategoryLoading(true);
    dispensingService
      .select({ studyid: selectedStudyId })
      .then((res) => {
        const list = res?.data || [];
        setInventoryList(list);
        if (list.length > 0) {
          setValue("ipcategory", list[0].ipcategory);
        } else {
          setValue("ipcategory", null);
        }
      })
      .catch((err) => {
        console.error("Inventory fetch failed:", err);
        setInventoryList([]);
        setValue("ipcategory", null);
      })
      .finally(() => {
        setIsIpCategoryLoading(false);
      });
  }, [selectedStudyId, setValue]);

  const ipCategoryOptions = useMemo(() => {
    const categories = Array.from(
      new Set(inventoryList.map((item: any) => item.ipcategory).filter(Boolean)),
    );
    return categories.map((cat: any) => ({
      label: cat,
      value: cat,
    }));
  }, [inventoryList]);

  const { DropdownQuery } = useReturnsAccountability({
    getStudyDropDown: true,
  });

  const studyOptions = useMemo(
    () =>
      (DropdownQuery.data as any)?.data?.map((study: any) => ({
        label: study.studycode,
        value: study.studyid,
      })) || [],
    [DropdownQuery.data],
  );

  const selectedStudyLabel =
    studyOptions.find((opt: any) => opt.value === Number(selectedStudyId))
      ?.label || "";

  const { subjectDropDown } = useReturnsAccountability({
    studyid: selectedStudyId ? Number(selectedStudyId) : null,
    ipcategory: selectedIpCategory,
  });

  const subjectOptions = useMemo(() => {
    const subjects = (subjectDropDown.data as any)?.data || [];
    return subjects
      .filter((item: any) => item.subjectno)
      .map((item: any) => ({
        label: String(item.subjectno),
        value: String(item.subjectno),
      }))
      .filter(
        (item: any, index: number, self: any[]) =>
          index === self.findIndex((t) => t.value === item.value),
      );
  }, [subjectDropDown.data]);

  useEffect(() => {
    if (subjectOptions.length > 0) {
      if (
        !selectedSubjectNo ||
        !subjectOptions.find((o: any) => o.value === selectedSubjectNo)
      ) {
        setValue("subjectno", subjectOptions[0].value);
      }
    } else {
      setValue("subjectno", null);
    }
  }, [subjectOptions, selectedSubjectNo, setValue]);

  const [tableData, setTableData] = useState<any[]>([]);

  const { SelectAllQuery: dispensingDetailsQuery, UpdateLabelPrintMutation } =
    useDispensingLabelService({
      studyid: selectedStudyId ? Number(selectedStudyId) : null,
      searchText: searchText,
      subjectno: selectedSubjectNo,
      ipcategory: selectedIpCategory,
    });

  useEffect(() => {
    if (dispensingDetailsQuery.data) {
      const apiData = ((dispensingDetailsQuery.data as any)?.data || [])
        .map((v: any) => ({
          ...v,
          subjectno: selectedSubjectNo,
          barcode: v.barcode || v.subjectno || null,
          period: v.period || "01",
          ip: v.productname || "N/A",
          dosageform: v.dosageform || "N/A",
          strength: v.strength || "N/A",
          batchno: v.batchlotno || "N/A",
          expdate: v.expirydate || null,
          instructions: v.instructions || "Take with water.",
          initials: v.initials || "N/A",
          dispensingnumber: v.dispensingnumber || "N/A",
        }))
        .filter((v: any) => !v.labelprintid);

      setTableData(apiData);
    } else {
      setTableData([]);
    }
  }, [dispensingDetailsQuery.data, selectedSubjectNo]);

  // Auto-fill top fields based on first row of tableData
  useEffect(() => {
    if (tableData.length > 0) {
      const firstRow = tableData[0];
      setValue("ip", firstRow.ip);
      setValue(
        "dosage_strength",
        `${firstRow.dosageform} - ${firstRow.strength}`,
      );
      setValue("batchno", firstRow.batchno);
      setValue(
        "expdate",
        firstRow.expdate ? formatDate(firstRow.expdate) : "N/A",
      );
    } else {
      setValue("ip", null);
      setValue("dosage_strength", null);
      setValue("batchno", null);
      setValue("expdate", null);
    }
  }, [tableData, setValue]);

  const getCountForId = (id: number) => individualCounts[id] || 1;

  // Fetch Dispensing Template
  useEffect(() => {
    if (selectedStudyId) {
      fetchTemplate();
    }
  }, [selectedStudyId]);

  const fetchTemplate = async () => {
    try {
      setIsTemplateLoading(true);
      const res = await templateApiService.getSelectAllData({
        searchtext: "Dispensing Label",
        pagenumber: 1,
        pagesize: 10,
        status: "A",
      });
      const templates = res?.data || [];
      const labelTemplate = templates.find((t: any) => {
        const name = String(t.templatename || t.name || "").toLowerCase();
        return name.includes("dispensing") || name.includes("drug label");
      });

      if (labelTemplate) {
        setTemplateHtml(labelTemplate.json || labelTemplate.htmlcontent || "");
      } else if (templates.length > 0) {
        setTemplateHtml(templates[0].json || templates[0].htmlcontent || "");
      }
    } catch (error) {
      console.error("Error fetching template", error);
    } finally {
      setIsTemplateLoading(false);
    }
  };

  const selectedAssignmentIds = useMemo(
    () =>
      selectedRows.map(
        (row) => row.dispensingsubjectid || row.volunteerassignmentid,
      ),
    [selectedRows],
  );

  const toggleSelection = (row: any, isChecked: boolean) => {
    setSelectedRows((prev) => {
      const next = isChecked
        ? [...prev, row]
        : prev.filter(
          (r) =>
            (r.dispensingsubjectid || r.volunteerassignmentid) !==
            (row.dispensingsubjectid || row.volunteerassignmentid),
        );

      if (isChecked) {
        // Find the index in the 'next' array to set it as current preview
        const idx = next.findIndex(
          (r) =>
            (r.dispensingsubjectid || r.volunteerassignmentid) ===
            (row.dispensingsubjectid || row.volunteerassignmentid),
        );
        if (idx !== -1) setCurrentIndex(idx);
      } else {
        setCurrentIndex(0);
      }

      return next;
    });
  };


  const mapSubjectToData = (row: any) => {
    const barcodeValue =
      row.dispensingnumber || row.barcode || row.subjectno || "123456789";
    const barcodeHtml = `<div class="barcode-target" data-barcode="${barcodeValue}"></div>`;

    return {
      SubjectNo: row.subjectno || "N/A",
      Initials: row.initials || "N/A",
      StudyCode: selectedStudyLabel || "N/A",
      Period: row.period || "N/A",
      InvestigationalProduct: row.ip || row.productname || "N/A",
      DosageForm: row.dosageform || "N/A",
      Strength: row.strength || "N/A",
      BatchNumber: row.batchno || row.batchlotno || "N/A",
      ExpirationDate: row.expdate ? formatDate(row.expdate) : "N/A",
      DosingInstructions: row.instructions || "N/A",
      Barcode: "___BARCODE_MARKER___",
      barcodeno: row.dispensingnumber || row.barcode || row.subjectno || "N/A",
      BarcodeHtml: barcodeHtml,
    };
  };


  // Preview Logic
  const previewList = selectedRows.length > 0 ? selectedRows : [];
  const currentSubject = previewList[currentIndex];

  const replacePlaceholders = (template: string, data: Record<string, any>) => {
    return template.replace(/{{(.*?)}}/g, (_, key) => {
      return data[key.trim()] ?? "";
    });
  };

  const mappedPreviewHtml = useMemo(() => {
    if (!templateHtml || !currentSubject) return null;

    const mappedData = mapSubjectToData(currentSubject);

    let renderedHtml = replacePlaceholders(templateHtml, mappedData);
    renderedHtml = DOMPurify.sanitize(renderedHtml, {
      ADD_TAGS: ["style"],
      WHOLE_DOCUMENT: true,
      FORCE_BODY: true,
    });

    return renderedHtml.replace("___BARCODE_MARKER___", mappedData.BarcodeHtml);
  }, [templateHtml, currentSubject, selectedStudyLabel]);

  // Barcode Rendering Effect - useLayoutEffect for zero-blink rendering
  useLayoutEffect(() => {
    if (previewRef.current && currentSubject) {
      const targets = previewRef.current?.querySelectorAll(".barcode-target");
      targets?.forEach((target) => {
        const val = target.getAttribute("data-barcode") || "";
        // Only render if target is empty to prevent blinking
        if (target.children.length === 0) {
          const root = createRoot(target as HTMLElement);
          root.render(
            <Barcode
              value={val}
              format="CODE128"
              width={1.5}
              height={45}
              displayValue={false}
              margin={0}
            />,
          );
        }
      });
    }
  }, [
    mappedPreviewHtml,
    selectedRows,
    individualCounts,
    dispensingDetailsQuery.isFetching,
    UpdateLabelPrintMutation.isPending,
  ]);

  const handlePrint = () => {
    if (selectedRows.length === 0) return;

    const payload = {
      dispensingprintdetail: selectedRows
        .filter(
          (row) =>
            row.dispensingsubjectid !== null &&
            row.dispensingsubjectid !== undefined,
        )
        .map((row) => ({
          id: row.dispensingsubjectid,
          quantity: getCountForId(row.dispensingsubjectid),
        })),
      studyid: selectedStudyId ? Number(selectedStudyId) : null,
    };

    UpdateLabelPrintMutation.mutate(payload, {
      onSuccess: (res: any) => {
        if (res.statusCode === 200) {
          showToastMessage("success", res.message || " Label Print request successfully added to queue! ");
          queryClient.invalidateQueries({
            queryKey: ["dispensing-label-SelectAll"],
          });
          setSelectedRows([]);
          setIndividualCounts({});
        } else {
          showToastMessage(
            "error",
            res.message || "Failed to update print status",
          );
        }
      },
      onError: (error: any) => {
        showToastMessage(
          "error",
          error.message || "Error updating print status",
        );
      },
    });
  };

  // Removed onPrintSuccess logic as it is merged into handlePrint above
  const { hasPermission } = usePermission();
  return (
    <Row>
      <Col lg={12}>
        {/* Study Mismatch Warning */}
        <GlobalStudyMismatchWarning
          globalStudy={selectedStudy}
          studyOptions={studyOptions}
          isLoaded={!DropdownQuery.isLoading}
        />
      </Col>
      {/* Left Column - Form & Table */}
      <Col lg={7}>
        <div className="mb-4">
          <CommonForm
            fields={dispensingFields(studyOptions, ipCategoryOptions, subjectOptions, !!selectedStudyId, isIpCategoryLoading || DropdownQuery.isLoading)}
            control={control}
            errors={errors}
            columns={3}
          />
        </div>

        {selectedStudyId && selectedSubjectNo && (
          <div className="mt-4">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h6
                className="fw-700 mb-0 small text-uppercase ls-1"
                style={{ color: "#008080" }}
              >
                Dispensing Details
              </h6>
              <div
                className={`${sf.searchBox} ${searchFocused ? sf.focused : ""}`}
                style={{ width: "250px", marginBottom: 0 }}
              >
                <i className={`ri-search-line ${sf.searchIcon}`} />
                <input
                  type="text"
                  placeholder="Search..."
                  value={searchText}
                  className={sf.searchInput}
                  onChange={(e) => setSearchText(e.target.value)}
                  onFocus={() => setSearchFocused(true)}
                  onBlur={() => setSearchFocused(false)}
                />
                {searchText && (
                  <i
                    className={`ri-close-line ${sf.clearIcon}`}
                    onClick={() => setSearchText("")}
                  />
                )}
              </div>
            </div>
            <CommonTableIndexWise
              columns={dispensingColumns(selectedAssignmentIds, toggleSelection)}
              data={tableData}
              className={tableStyles.table}
              hideAction={true}
              maxHeight="400px"
              stickyHeader={true}
              loading={
                dispensingDetailsQuery.isLoading ||
                dispensingDetailsQuery.isFetching
              }
            />
          </div>
        )}
      </Col>

      {/* Right Column - Preview */}
      {selectedStudyId && selectedSubjectNo && (
        <Col lg={5}>
          <Card className="border shadow-none rounded-4 overflow-hidden">
            <CardBody className="p-3 d-flex flex-column">
              <div className="mb-3">
                <h6 className="fw-700 mb-1">Label Preview</h6>
                <p className="text-muted extra-small mb-0">
                  Dispensing Label Preview
                </p>
              </div>

              <div className={styles.preview_container} ref={previewRef}>
                {isTemplateLoading ? (
                  <div className="text-center py-4">
                    <Spinner size="sm" color="primary" />
                  </div>
                ) : currentSubject ? (
                  <div
                    className={styles.simulated_label_wrapper}
                    dangerouslySetInnerHTML={{
                      __html: mappedPreviewHtml || "",
                    }}
                  />
                ) : (
                  <div className="text-center text-muted p-4 border rounded-3 bg-white w-100">
                    <i className="ri-flask-line fs-1 d-block mb-2 opacity-25" />
                    <p className="small mb-0">
                      Select a subject from the list to preview their label
                    </p>
                  </div>
                )}
              </div>

              {previewList.length > 1 && (
                <div className="mt-3">
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <Button
                      color="light"
                      size="sm"
                      className="rounded-3 p-0 d-flex align-items-center justify-content-center border"
                      style={{
                        width: "32px",
                        height: "32px",
                        backgroundColor: "#fff",
                      }}
                      onClick={() =>
                        setCurrentIndex(Math.max(0, currentIndex - 1))
                      }
                      disabled={currentIndex === 0}
                    >
                      <i className="ri-arrow-left-s-line fs-5" />
                    </Button>

                    <div className="px-3 py-1 bg-light rounded-3">
                      <span className="fw-700 small text-primary">
                        {currentIndex + 1} of {previewList.length}
                      </span>
                    </div>

                    <Button
                      color="light"
                      size="sm"
                      className="rounded-3 p-0 d-flex align-items-center justify-content-center border"
                      style={{
                        width: "32px",
                        height: "32px",
                        backgroundColor: "#fff",
                      }}
                      onClick={() =>
                        setCurrentIndex(
                          Math.min(previewList.length - 1, currentIndex + 1),
                        )
                      }
                      disabled={currentIndex === previewList.length - 1}
                    >
                      <i className="ri-arrow-right-s-line fs-5" />
                    </Button>
                  </div>
                </div>
              )}
              {hasPermission(PERMISSION_MODULES.DISPENSING_LABEL, PERMISSION_ACTIONS.PRINT) && (
                <div className="d-flex justify-content-between align-items-center mb-3 px-1">
                  <span className="text-muted small fw-semibold">Quantity</span>
                  <div className="d-flex align-items-center gap-3">
                    <Button
                      size="sm"
                      color="light"
                      className="rounded-circle p-0 d-flex align-items-center justify-content-center border shadow-sm"
                      style={{
                        width: "28px",
                        height: "28px",
                        backgroundColor: "#fff",
                      }}
                      onClick={() => {
                        if (!currentSubject) return;
                        const id = currentSubject.dispensingsubjectid;
                        setIndividualCounts((prev) => ({
                          ...prev,
                          [id]: Math.max(1, getCountForId(id) - 1),
                        }));
                      }}
                      disabled={
                        !currentSubject ||
                        getCountForId(currentSubject.dispensingsubjectid) <= 1
                      }
                    >
                      <i className="ri-subtract-line fs-6" />
                    </Button>
                    <span
                      className="fw-bold text-primary"
                      style={{ minWidth: "20px", textAlign: "center" }}
                    >
                      {currentSubject
                        ? getCountForId(currentSubject.dispensingsubjectid)
                        : 0}
                    </span>
                    <Button
                      size="sm"
                      color="light"
                      className="rounded-circle p-0 d-flex align-items-center justify-content-center border shadow-sm"
                      style={{
                        width: "28px",
                        height: "28px",
                        backgroundColor: "#fff",
                      }}
                      onClick={() => {
                        if (!currentSubject) return;
                        const id = currentSubject.dispensingsubjectid;
                        setIndividualCounts((prev) => ({
                          ...prev,
                          [id]: getCountForId(id) + 1,
                        }));
                      }}
                      disabled={!currentSubject}
                    >
                      <i className="ri-add-line fs-6" />
                    </Button>
                  </div>
                </div>
              )}

              <CommonButton
                module={PERMISSION_MODULES.DISPENSING_LABEL}
                action={PERMISSION_ACTIONS.PRINT}
                color="primary"
                block
                className="py-2 rounded-3 d-flex align-items-center justify-content-center gap-2 fw-700 shadow-sm mt-auto"
                disabled={
                  previewList.length === 0 || UpdateLabelPrintMutation.isPending
                }
                onClick={handlePrint}
              >
                {UpdateLabelPrintMutation.isPending ? (
                  <Spinner size="sm" />
                ) : (
                  <i className="ri-printer-line" />
                )}{" "}
                Print Labels
              </CommonButton>
            </CardBody>
          </Card>
        </Col>
      )}

      {/* Modal removed as browser print is skipped */}
    </Row>
  );
};

export default DispensingLabelList;
